import { useAuthState } from '@packages/contexts/auth';
import React, { createContext, useContext, useState, useCallback } from 'react';

type BatteryData = {
    itemCode: string;
    id: string;
    partNumber: string;
    productionDate: string;
    serialNumber: string;
    fullCode: string;
    format: string;
    isValid: boolean;
};

type FormData = {
    selectedRetailer: any;
    repairOrderNumber?: string;
    oldBinQrCode: string;
    newBinQrCode: string;
    parsedOldBinData?: BatteryData;
    parsedNewBinData?: BatteryData;
    [key: string]: any; // allows adding new fields easily
};

type ErrorMessages = {
    [key: string]: any; // allows adding new fields easily
};

type FormContextType = {
    data: FormData;
    errorMessage: ErrorMessages;
    updateData: (values: Partial<FormData>) => void;
    resetForm: () => void;
    parseBatteryCode: (code: string) => BatteryData;
    validateForm: (stepToValidate: number) => boolean;
    setOldBinFromExisting: (existingBin?: string) => void;
};

const FormContext = createContext<FormContextType | undefined>(undefined);

// ---- helper lives above updateData so it can be used there ----
function parseBatteryCodeImpl(code: string): BatteryData {
    const cleanData = (code || '').replace(/\s+/g, '').toUpperCase();

    // 32-digit QR: P600D82005FN020240619000000111200
    if (/^[A-Z]+[0-9A-Z]{31}$/.test(cleanData) && cleanData.length === 32) {
        return {
            itemCode: cleanData.slice(0, 2),
            id: cleanData.slice(2, 5),
            partNumber: cleanData.slice(5, 15),
            productionDate: cleanData.slice(15, 21),
            serialNumber: cleanData.slice(21, 32),
            fullCode: cleanData,
            format: '32-Digit QR Code',
            isValid: true,
        };
    }

    // 24-digit QR Code
    if (/^[0-9A-Z]{24}$/.test(cleanData) && cleanData.length === 24) {
        const yearCode = cleanData.charAt(14);
        const monthCode = cleanData.charAt(15);
        const dayCode = cleanData.charAt(16);

        const decodeYear = (c: string): number => {
            const baseYear = 2011;
            const charCode = c.charCodeAt(0);
            let offset = 0;

            if (/[0-9]/.test(c)) {
                offset = parseInt(c, 10) - 1;
            } else if (/[A-Z]/.test(c)) {
                offset = charCode - 65 + 9; // A=10..Z=35 -> 9..34
            }
            const cycle = Math.floor(offset / 31);
            const yearInCycle = offset % 31;
            return baseYear + yearInCycle + cycle * 31;
        };

        const decodeMonth = (c: string): string => {
            if (/[0-9]/.test(c)) return c.padStart(2, '0');
            const monthVal = c.charCodeAt(0) - 55; // A=10
            return String(monthVal).padStart(2, '0');
        };

        const decodeDay = (c: string): string => {
            if (/[0-9]/.test(c)) return c.padStart(2, '0');
            const dayVal = c.charCodeAt(0) - 64; // A=1
            return String(dayVal).padStart(2, '0');
        };

        const year = decodeYear(yearCode);
        const month = decodeMonth(monthCode);
        const day = decodeDay(dayCode);
        const productionDate = `${String(year).slice(-2)}${month}${day}`;

        return {
            itemCode: cleanData.slice(0, 2),
            id: cleanData.slice(2, 4),
            partNumber: '',
            productionDate,
            serialNumber: cleanData.slice(17, 24),
            fullCode: cleanData,
            format: '24-Digit QR Code',
            isValid: true,
        };
    }

    // 17-digit barcode: 20240619000111200
    if (/^[0-9]{17}$/.test(cleanData) && cleanData.length === 17) {
        return {
            itemCode: '',
            id: '',
            partNumber: '',
            productionDate: cleanData.slice(0, 8),
            serialNumber: cleanData.slice(8, 17),
            fullCode: cleanData,
            format: '17-Digit Barcode',
            isValid: true,
        };
    }

    return {
        itemCode: '',
        id: '',
        partNumber: '',
        productionDate: '',
        serialNumber: code,
        fullCode: code,
        format: 'Unknown',
        isValid: false,
    };
}

export const FormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [data, setData] = useState<FormData>({
        selectedRetailer: undefined,
        repairOrderNumber: '',
        oldBinQrCode: '',
        newBinQrCode: '',
        parsedOldBinData: undefined,
        parsedNewBinData: undefined,
    });

    const [errorMessage, setErrorMessage] = useState<ErrorMessages>({});

    const updateData = useCallback((values: Partial<FormData>) => {
        setErrorMessage((prevErrors) => {
            let tErrorMessage = { ...prevErrors };

            if (values.repairOrderNumber && values.repairOrderNumber.length > 0) {
                tErrorMessage = { ...tErrorMessage, repairOrderNumber: '' };
            }
            if (values.selectedRetailer && (values.selectedRetailer as any)?.length > 0) {
                tErrorMessage = { ...tErrorMessage, selectedRetailer: '' };
            }

            return tErrorMessage;
        });

        // auto-calculate parsed payloads when QR codes are updated
        const next: Partial<FormData> = { ...values };

        if ('newBinQrCode' in values) {
            next.parsedNewBinData = parseBatteryCodeImpl(values.newBinQrCode || '');
        }
        if ('oldBinQrCode' in values) {
            next.parsedOldBinData = parseBatteryCodeImpl(values.oldBinQrCode || '');
        }

        setData((prev) => ({
            ...prev,
            ...next, // merge updated values plus any parsed data
        }));
    }, []);

    const resetForm = useCallback(
        () =>
            setData({
                selectedRetailer: '',
                repairOrderNumber: '',
                oldBinQrCode: '',
                newBinQrCode: '',
                parsedOldBinData: undefined,
                parsedNewBinData: undefined,
            }),
        []
    );

    const validateForm = useCallback(
        (stepToValidate: number) => {
            if (stepToValidate === 2) {
                if (
                    !data.repairOrderNumber ||
                    data.repairOrderNumber?.length < 0 ||
                    data.repairOrderNumber?.length > 10 ||
                    !data.selectedRetailer ||
                    data.selectedRetailer?.length === 0
                ) {
                    setErrorMessage((prevErrors) => {
                        let tErrorMessage: any = { ...prevErrors };
                        if (
                            !data.repairOrderNumber ||
                            data.repairOrderNumber?.length < 4 ||
                            data.repairOrderNumber?.length > 10
                        ) {
                            tErrorMessage = { ...tErrorMessage, repairOrderNumber: 'Please Enter Repair Order Number' };
                        }
                        if (!data.selectedRetailer || data.selectedRetailer?.length === 0) {
                            tErrorMessage = { ...tErrorMessage, selectedRetailer: 'Please Select Retailer' };
                        }
                        return tErrorMessage;
                    });
                    return false;
                }
            }
            return true;
        },
        [data.repairOrderNumber, data.selectedRetailer]
    );

    // expose the same signature used elsewhere
    const parseBatteryCode = useCallback((code: string) => parseBatteryCodeImpl(code), []);

    const setOldBinFromExisting = useCallback(
        (existingBin?: string) => {
            if (existingBin && existingBin.length > 0) {
                updateData({ oldBinQrCode: existingBin });
            }
        },
        [updateData]
    );

    return (
        <FormContext.Provider
            value={{ data, errorMessage, updateData, resetForm, parseBatteryCode, validateForm, setOldBinFromExisting }}
        >
            {children}
        </FormContext.Provider>
    );
};

export const useFormContext = () => {
    const context = useContext(FormContext);
    if (!context) throw new Error('useFormContext must be used within a FormProvider');
    return context;
};
